/**
 * @file This file is a helper class for its sibling class Table.ts. It provides the logic needed to support the multiEdit property (in LR and ALP).
 */

import type { Definition } from 'typescript-json-schema';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import i18next from 'i18next';
import type {
    AccessorPath,
    GenerateAppSchemaParameters,
    ProcessingRule,
    SchemaHandlingParams,
    SyncRule
} from '@sap/ux-specification-types';
import * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Table';
import { syncRule } from '../../../common/decoration';
import { addEntityTypeFieldsAsEnum, getManifestPathForTableSettings, getTableType, type TableParams } from './Table';
import { getManifestPropertyByPath } from '../../utils';
import {
    getSyncRuleForManifestProperty,
    handleDisallowedProperty,
    hasPropertyTypeError
} from '../../generate/manifestPropertyUtils';
import type { ManifestRule } from '../../generate/manifestPropertyUtils';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { getListOfValidAnnotations } from '../../../common';

/**
 * An instance of this type contains the information relevant for dealing with the multiEdit section in the manifest.
 * Its instances will be created in the processingRuleAdapter of class MultiEdit and consumed in the processingRuleAdapters of the properties of this class.
 *
 * @property enabled - is multiEdit currently enabled (redundant, as it can also be derived from multiEdit property)
 * @property multiEdit - the multiEdit content from the manifest as a whole
 * @property entityTypeDefinition - the entity type this setting will be applied to
 * @property manifestPath - Path in manifest where multiEdit would be found
 */
type MultiEditInfo = {
    enabled: boolean;
    multiEdit?: object;
    entityTypeDefinition: Edm.EntityType;
    manifestPath: AccessorPath;
};

/**
 * This function adapts getSyncRuleForManifestProperty for properties belonging to the multiEdit object inside the tableSettings.
 *
 * @param property - the property name inside multiEdit
 * @param propertyIndex - index of the property inside multiEdit (used to achieve an ordering which is more logical than alphabetical)
 * @param expectedType -type of the property
 * @param onlyEnabled - whether the property is only allowed when multiEdit is enabled
 * @param addEnum - optional function for adding enum values to the property definition
 * @returns the SyncRule for the multiEdit property
 */
function getSyncRuleForMultiEditProperty(
    property: string,
    propertyIndex: number,
    expectedType: string,
    onlyEnabled: boolean = false,
    addEnum?: (bluePrint: Definition, entityTypeDefinition: Edm.EntityType) => void
): SyncRule {
    const analyze = function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
        const multiEditInfo = schemaHandlingParams.specificParams as MultiEditInfo;
        const rule: ManifestRule = {
            manifestPath: multiEditInfo.manifestPath
        };
        if (onlyEnabled && !multiEditInfo.enabled) {
            rule.disallowedReason = i18next.t('PROPERTYONLYMULTIEDIT', { property });
        }
        return rule;
    }; // analyze
    const adapt = function (
        element: Definition,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ) {
        element['propertyIndex'] = propertyIndex;
        if (addEnum) {
            const multiEditInfo = schemaHandlingParams.specificParams as MultiEditInfo;
            if (multiEditInfo.entityTypeDefinition) {
                addEnum(element, multiEditInfo.entityTypeDefinition);
            }
        }
    };
    return getSyncRuleForManifestProperty(property, analyze, adapt, undefined, expectedType);
}

// MultiEdit
// This is the type which is used for describing the multiEdit manifest property (only in case this property is allowed for the current table)
@syncRule({
    processingRuleAdapter(
        processingRule: ProcessingRule,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): void {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        const settings = tableParams.multiTableInfo?.variantInfo
            ? tableParams.multiTableInfo.variantInfo.variantDefinition
            : (tableParams.settings as object | undefined);
        const tableSettings = settings?.['tableSettings'];
        const hasTypeError = hasPropertyTypeError(tableSettings, 'multiEdit', generateParameters.logger, 'object');
        if (hasTypeError) {
            delete processingRule.element;
            return;
        }
        const multiEdit = tableSettings?.multiEdit;
        const enabled = !!multiEdit?.enabled;
        const entityTypeDefinition = tableParams.multiTableInfo?.variantInfo
            ? tableParams.multiTableInfo.variantInfo.entityTypeDefinition
            : tableParams.entityType;
        const manifestPath = getManifestPathForTableSettings(schemaHandlingParams, tableParams);
        manifestPath.push('multiEdit');
        const multiEditInfo: MultiEditInfo = {
            enabled,
            multiEdit,
            entityTypeDefinition,
            manifestPath
        };
        processingRule.referenceAdaptation = {
            specificParams: multiEditInfo
        };
    }
})
export class MultiEdit implements v2.MultiEdit {
    // enabled
    @syncRule(getSyncRuleForMultiEditProperty('enabled', 0, 'boolean'))
    enabled?: boolean;

    // annotationPath
    @syncRule(
        getSyncRuleForMultiEditProperty(
            'annotationPath',
            1,
            'string',
            true,
            function (element: Definition, entityTypeDefinition: Edm.EntityType) {
                const fieldGroups = getListOfValidAnnotations(entityTypeDefinition, [UIAnnotationTerms.FieldGroup]);
                element.enum = fieldGroups;
            }
        )
    )
    annotationPath?: string;

    // ignoredFields
    @syncRule(
        getSyncRuleForMultiEditProperty(
            'ignoredFields',
            2,
            'string',
            true,
            function (bluePrint: Definition, entityTypeDefinition: Edm.EntityType) {
                addEntityTypeFieldsAsEnum(bluePrint, entityTypeDefinition);
            }
        )
    )
    ignoredFields?: v2.IgnoredFieldsType[];
}

export const syncRuleMultiEdit: SyncRule = {
    processingRuleAdapter(
        processingRule: ProcessingRule,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): void {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        const tableType = getTableType(tableParams);
        let disallowedReason: string | undefined;
        if (tableParams.settings?.isWorklist) {
            disallowedReason = i18next.t('MULTIEDITNOWORKLIST');
        } else if (tableParams.multiTableInfo?.variantInfo?.isChart) {
            disallowedReason = i18next.t('MULTIEDITNOCHART');
        } else if (tableType === v2.TableTypeV2Enum.AnalyticalTable || tableType === v2.TableTypeV2Enum.TreeTable) {
            disallowedReason = i18next.t('MULTIEDITTABLETYPE');
        }
        if (disallowedReason) {
            const manifestPath = getManifestPathForTableSettings(schemaHandlingParams, tableParams);
            const { value } = getManifestPropertyByPath(generateParameters.manifest, manifestPath, 'multiEdit');
            handleDisallowedProperty(disallowedReason, processingRule, value, manifestPath);
            return;
        }
        processingRule.referenceAdaptation = {
            suffix: tableParams.multiTableInfo?.variantInfo
                ? tableParams.multiTableInfo.variantInfo.iconTabFilterKey
                : undefined,
            syncRuleProvider: MultiEdit
        };
    }
};
